import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	SHOW_ONLY_ACTIVES_DEFAULT,
	ShowOnlyActivesCheckbox,
} from "@/controls/filters/ShowOnlyActivesCheckbox";
import { Paper } from "@mui/material";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import { useCategories } from "../CatalogCategoriesPage";
import { useCategoriesOptionsFunctions } from "../hooks/useCategoriesOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { DelayCategory } from "../models/CatalogCategory";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

// A row click opens a modal, and modals stay out of the URL, so only the filter travels.
const URL_SCHEMA = {
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
};

export const TableCategories = ({
	categoryId,
}: {
	categoryId: string | undefined;
}) => {
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [selectedRowData, setSelectedRowData] = useState<DelayCategory | null>(
		null,
	);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");
	const [{ onlyActives }, setFilters] = useUrlState(URL_SCHEMA);

	// Always read the whole catalog and narrow it here, so the checkbox does not
	// go back to the server on every click.
	const { data: rows, isLoading } = useCategories({
		includeInactives: true,
		onError: (error) => setError(error.message),
	});

	const visibleRows = useMemo(() => {
		const all = rows ?? [];
		return onlyActives ? all.filter((c) => c.IsActive) : all;
	}, [rows, onlyActives]);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useCategoriesOptionsFunctions({
		setSelectedRowData,
		setShowModal,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowDoubleClicked = (event: any) => {
		setSelectedRowData(event.data);
		setShowModal("update");
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "categoriesContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "categoriesContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2
					container
					size={{ md: 12, xs: 12 }}
					spacing={1}
					direction={"column"}
				>
					<Grid2>
						<Typography variant="h5" fontWeight={600}>
							Stops Categories
						</Typography>
					</Grid2>
					<Grid2 style={{ width: "fit-content" }}>
						<ShowOnlyActivesCheckbox
							checked={onlyActives}
							onChange={(value) => setFilters({ onlyActives: value })}
						/>
					</Grid2>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper elevation={2} style={{ height: "64vh", width: "100%" }}>
							<AgGridReact<DelayCategory>
								loading={isLoading}
								rowData={visibleRows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={30}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								onGridReady={(params) => setGridApi(params.api)}
								loadingOverlayComponent={CenteredLazyLoading}
								getRowId={(params) => `${params.data.DelayCategoryId}`}
								getRowClass={(params) =>
									params.data && !params.data.IsActive
										? "ag-row-inactive"
										: undefined
								}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
								}}
							/>
						</Paper>
					</Grid2>
					<FooterButton
						label="CATEGORY"
						showCreateModal={showModal === "create"}
						setShowCreateModal={(showCreateModal) =>
							showCreateModal ? setShowModal("create") : setShowModal("")
						}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<UpsertDeleteModal
				delayCategory={selectedRowData}
				modal={showModal}
				onHide={() => {
					setShowModal("");
				}}
			/>
		</>
	);
};
